import { handleRequest } from '../utilities/requestHandler';

export async function fetchNotifications({ unreadOnly = false, limit = 20, offset = 0, q = "", status = "", sort = "" } = {}) {
  const params = {
    unreadOnly,
    limit,
    offset,
  };
  if (q) {
    params.q = q;
  }
  if (status) {
    params.status = status;
  }
  if (sort) {
    params.sort = sort;
  }
  const response = await handleRequest("get", "/notifications", params);
  const fallback = {
    items: [],
    total: 0,
    limit,
    offset,
  };

  if (Array.isArray(response?.data)) {
    return {
      items: response.data,
      total: typeof response.total === 'number' ? response.total : response.data.length,
      limit: typeof response.limit === 'number' ? response.limit : limit,
      offset: typeof response.offset === 'number' ? response.offset : offset,
    };
  }

  if (Array.isArray(response)) {
    return { ...fallback, items: response, total: response.length };
  }

  return fallback;
}

export async function fetchUnreadCount() {
  const { data } = await handleRequest("get", "/notifications/unread-count");
  return data?.count || 0;
}

export async function markRead(recipientIds = []) {
  if (!recipientIds.length) return { updated: 0 };
  const { data } = await handleRequest("post", "/notifications/mark-read", {
    recipient_ids: recipientIds
  });
  return { updated: data?.updated || 0 };
}

/**
 * Ask the server to mark every notification visible to the current user as read.
 *
 * Sends no recipient ids on purpose: `GET /notifications` caps a page at 100 rows
 * server-side, so an id list gathered from the loaded items can only cover that
 * page and leaves the unread badge stuck at the un-fetched remainder.
 *
 * @returns {Promise<{updated: number, unread: number|null}>} Rows updated, and the
 *   authoritative unread count after the write. `unread` is null when the server
 *   did not return a usable number (handleRequest swallows errors and resolves
 *   with the error payload), so callers must not treat a failure as "zero unread".
 */
export async function markAllRead() {
  const { data } = await handleRequest("post", "/notifications/mark-all-read", {});
  const remaining = Number(data?.unread);
  return {
    updated: data?.updated || 0,
    unread: Number.isFinite(remaining) ? remaining : null,
  };
}

export async function markUnread(recipientIds = []) {
  if (!recipientIds.length) return { updated: 0 };
  const { data } = await handleRequest("post", "/notifications/mark-unread", {
    recipient_ids: recipientIds
  });
  return { updated: data?.updated || 0 };
}

export async function deleteNotifications(recipientIds = []) {
  if (!recipientIds.length) return { deleted: 0, unread_deleted: 0 };
  const { data } = await handleRequest("post", "/notifications/delete", {
    recipient_ids: recipientIds
  });
  return { deleted: data?.deleted || 0, unread_deleted: data?.unread_deleted || 0 };
}

/**
 * Ask the server to delete every already-read notification for the current user.
 *
 * Sends no recipient ids, for the same reason as `markAllRead`: `GET /notifications` caps a
 * page at 100 rows, so an id list gathered from the loaded items clears only that page. That
 * is what left the bell empty while the notifications page still listed everything.
 *
 * @returns {Promise<{deleted: number, unread: number|null}>} Rows deleted, and the
 *   authoritative unread count afterwards. `unread` is null when the server did not return a
 *   usable number (handleRequest swallows errors and resolves with the error payload), so
 *   callers must not treat a failure as "zero unread".
 */
export async function deleteAllRead() {
  const { data } = await handleRequest("post", "/notifications/delete-all-read", {});
  const remaining = Number(data?.unread);
  return {
    deleted: data?.deleted || 0,
    unread: Number.isFinite(remaining) ? remaining : null,
  };
}

export async function fetchCcrSubmission(submissionId) {
  if (!submissionId) return null;
  const response = await handleRequest("get", `/machines/contact-ccr/submissions/${submissionId}`);
  return response?.data?.submission || response?.submission || null;
}

export async function fetchPurchaseRequestSubmission(submissionId) {
  if (!submissionId) return null;
  const response = await handleRequest("get", `/orders/purchase-request/submissions/${submissionId}`);
  return response?.data?.submission || response?.submission || null;
}